import { useState } from 'react';

import { signInWithPin } from '../net/api';

import { getDeviceContext } from './deviceToken';
import { setOperator } from './operatorToken';
import { PinPad } from './PinPad';

/**
 * Operator sign-in: PIN → operator token (POST /floor/signin). Shown over the
 * app whenever nobody is signed in at this paired device — at the start of a
 * shift, after "Switch user", or when the server answered a write with
 * OPERATOR_REQUIRED / "Sign in again" (the 12 h token expired).
 *
 * The failure reply is generic on purpose (the API gives no enumeration help);
 * a 429 means the device is in PIN lockout and says so.
 */
export function SignInScreen(): JSX.Element {
  const context = getDeviceContext();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (pin: string): void => {
    setBusy(true);
    setError(null);
    signInWithPin(pin)
      .then((result) => {
        if (result.ok) {
          setOperator({
            token: result.data.token,
            employeeId: result.data.employeeId,
            displayName: result.data.displayName,
            expiresAt: result.data.expiresAt,
          });
          return;
        }
        setError(result.error.message);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Sign-in failed. Try again.');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className="kiosk signin">
      <header className="kiosk__header">
        <div className="kiosk__dept">WarehouseLogix</div>
        <div className="hub-site">{context !== null ? context.siteName : 'Paired device'}</div>
      </header>
      <div className="card signin__card">
        <h1>Enter your PIN</h1>
        <PinPad onSubmit={submit} disabled={busy} />
        {error !== null && (
          <p className="signin__error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
